import {
  BufferTarget,
  EncodedAudioPacketSource,
  EncodedPacket,
  EncodedVideoPacketSource,
  Mp4OutputFormat,
  Output,
  WebMOutputFormat,
  type AudioCodec,
  type VideoCodec,
} from 'mediabunny';
import type {
  ContainerFormat,
  EncodedAudioChunkLike,
  EncodedVideoChunkLike,
  MuxerAdapter,
} from '../../visual/index.js';

/**
 * The container, written by Mediabunny.
 *
 * The one file in the project that knows what an MP4 box or a Matroska cluster
 * is, and it does not know much: it converts a chunk into the library's packet
 * type, hands it over, and keeps the two orderings straight. Everything above it
 * sees `MuxerAdapter`.
 *
 * ## Why a library
 *
 * Writing an MP4 by hand is a week of work and a long tail of players that
 * almost open the result. The interesting parts of this phase are the timeline,
 * the interleave and the lifecycle, not the byte layout of `stsz`. Mediabunny
 * also *reads* files, which is what makes §25's verification possible without
 * trusting the thing that produced them.
 *
 * ## Buffering
 *
 * `BufferTarget` holds the finished file in memory. That is a real bound and it
 * is stated rather than hidden: an MP4's index has to be written after the media
 * it indexes, so a non-fragmented MP4 cannot be streamed to its final byte
 * without either seeking back or holding the file. For the lengths this exports
 * — a few minutes at a few megabytes a minute — memory is the simpler and
 * faster choice. The chunks themselves are still streamed: nothing accumulates
 * encoded video or audio waiting for the other track.
 */

export interface MediabunnyMuxerOptions {
  readonly container: ContainerFormat;
  readonly videoCodec: VideoCodec;
  readonly audioCodec?: AudioCodec;
  readonly width: number;
  readonly height: number;
  readonly framerate: number;
}

export class MediabunnyMuxer implements MuxerAdapter {
  readonly container: ContainerFormat;
  private readonly output: Output<Mp4OutputFormat | WebMOutputFormat, BufferTarget>;
  private readonly video: EncodedVideoPacketSource;
  private readonly audio: EncodedAudioPacketSource | null;
  private started = false;
  private finished = false;
  private aborted = false;
  private videoMetaSent = false;
  private audioMetaSent = false;
  private videoSequence = 0;
  private audioSequence = 0;

  constructor(options: MediabunnyMuxerOptions) {
    this.container = options.container;
    const format = options.container === 'mp4' ? new Mp4OutputFormat() : new WebMOutputFormat();
    this.output = new Output({ format, target: new BufferTarget() });

    this.video = new EncodedVideoPacketSource(options.videoCodec);
    // `frameRate` lets the muxer snap timestamps to the grid the export is
    // already on. Without it a container can record each frame's duration from
    // the gap to the next one, which makes the final frame's length a guess.
    this.output.addVideoTrack(this.video, { frameRate: options.framerate });

    if (options.audioCodec === undefined) {
      this.audio = null;
    } else {
      this.audio = new EncodedAudioPacketSource(options.audioCodec);
      this.output.addAudioTrack(this.audio);
    }
  }

  get hasAudioTrack(): boolean {
    return this.audio !== null;
  }

  async start(): Promise<void> {
    if (this.started) return;
    await this.output.start();
    this.started = true;
  }

  async addVideoChunk(chunk: EncodedVideoChunkLike): Promise<void> {
    if (!this.started || this.aborted) return;
    const packet = toPacket(chunk, this.videoSequence);
    this.videoSequence += 1;
    // The decoder config goes with the first packet only. Sending it again on
    // every packet is accepted but re-describes the track each time, which some
    // demuxers read as a format change mid-stream.
    const meta = this.videoMetaSent ? undefined : metaFrom(chunk.decoderConfig);
    if (meta !== undefined) this.videoMetaSent = true;
    await this.video.add(packet, meta as never);
  }

  async addAudioChunk(chunk: EncodedAudioChunkLike): Promise<void> {
    const audio = this.audio;
    if (audio === null || !this.started || this.aborted) return;
    const packet = toPacket(chunk, this.audioSequence);
    this.audioSequence += 1;
    const meta = this.audioMetaSent ? undefined : metaFrom(chunk.decoderConfig);
    if (meta !== undefined) this.audioMetaSent = true;
    await audio.add(packet, meta as never);
  }

  async finalize(): Promise<Uint8Array> {
    if (this.aborted) throw new Error('muxer was aborted');
    if (!this.started) throw new Error('muxer was never started');
    if (!this.finished) {
      await this.output.finalize();
      this.finished = true;
    }
    const buffer = this.output.target.buffer;
    if (buffer === null) throw new Error('muxer finalized without producing a buffer');
    return new Uint8Array(buffer);
  }

  /**
   * Abandons the file.
   *
   * `cancel` rather than `finalize`: the difference is the whole point of
   * having both. A cancelled export that finalized would produce a file with a
   * valid index over a truncated song, and nothing about opening it would
   * suggest it was incomplete.
   */
  async abort(): Promise<void> {
    if (this.aborted || this.finished) return;
    this.aborted = true;
    try {
      if (this.started) await this.output.cancel();
    } catch {
      // The output is being discarded; a failure to discard it cleanly is not
      // worth propagating over whatever caused the cancellation.
    }
  }

  dispose(): void {
    if (!this.finished && !this.aborted && this.started) {
      void this.output.cancel().catch(() => undefined);
      this.aborted = true;
    }
  }

  /** The MIME type of the file, including the codec strings. */
  async mimeType(): Promise<string> {
    return this.output.getMimeType();
  }
}

/**
 * Copies a chunk into a packet.
 *
 * The copy is unavoidable and it is per-chunk rather than per-file: the encoder
 * owns its chunk's memory and reuses it, and the muxer keeps what it is given
 * until the file is written. One allocation the size of one compressed frame.
 */
function toPacket(
  chunk: EncodedVideoChunkLike | EncodedAudioChunkLike,
  sequence: number,
): EncodedPacket {
  const bytes = new Uint8Array(chunk.byteLength);
  chunk.copyTo(bytes);
  return new EncodedPacket(
    bytes,
    chunk.type,
    // Seconds, from the microseconds the encoder reported — which came from the
    // frame index or the sample index, and from no clock.
    chunk.timestampMicros / 1_000_000,
    (chunk.durationMicros ?? 0) / 1_000_000,
    sequence,
  );
}

/** Presents an opaque decoder config as the metadata the library expects. */
function metaFrom(decoderConfig: unknown): { decoderConfig: unknown } | undefined {
  if (decoderConfig === undefined || decoderConfig === null) return undefined;
  return { decoderConfig };
}

/** Which containers this muxer can write. */
export function muxerSupports(container: ContainerFormat): boolean {
  return container === 'mp4' || container === 'webm';
}

/** The library's name for a codec, from the string an encoder was configured with. */
export function muxerVideoCodec(codec: string): VideoCodec | null {
  if (codec.startsWith('avc1') || codec.startsWith('avc3')) return 'avc';
  if (codec.startsWith('vp09')) return 'vp9';
  if (codec.startsWith('vp8')) return 'vp8';
  if (codec.startsWith('av01')) return 'av1';
  if (codec.startsWith('hev1') || codec.startsWith('hvc1')) return 'hevc';
  return null;
}

export function muxerAudioCodec(codec: string): AudioCodec | null {
  if (codec.startsWith('mp4a.40')) return 'aac';
  if (codec === 'opus') return 'opus';
  return null;
}
